/**
 * 報酬設定 (commission_sets) の hooks (v3.2.5 / T5)。
 *
 * 読込: Firestore 直接 (rules で全 admin が read 可能)
 * 書込: Callable 経由 (upsertCommissionSet / setCommissionItems / 他)
 */
import type {
  CommissionItem,
  CommissionList,
  CommissionListType,
  CommissionSet,
  CommissionType,
} from '@goenhub/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

import { db, functions } from '@/lib/firebase';

export interface CommissionSetRecord extends CommissionSet {
  id: string;
}

/** 全セット一覧 (作成日順)。 */
export function useCommissionSets() {
  return useQuery({
    queryKey: ['commission_sets'],
    queryFn: async (): Promise<CommissionSetRecord[]> => {
      const snap = await getDocs(
        query(collection(db, 'commission_sets'), orderBy('createdAt', 'asc')),
      );
      return snap.docs.map((d) => ({ id: d.id, ...(d.data() as CommissionSet) }));
    },
  });
}

/** 単一セット詳細 + 全 list (referrer/sa/employee) を一括取得。 */
export function useCommissionSetDetail(setId: string | null | undefined) {
  return useQuery({
    queryKey: ['commission_sets', 'detail', setId],
    enabled: !!setId,
    queryFn: async (): Promise<{
      set: CommissionSetRecord;
      lists: Record<CommissionListType, CommissionList | null>;
    } | null> => {
      if (!setId) return null;
      const setSnap = await getDoc(doc(db, 'commission_sets', setId));
      if (!setSnap.exists()) return null;
      const set: CommissionSetRecord = {
        id: setSnap.id,
        ...(setSnap.data() as CommissionSet),
      };
      const listsSnap = await getDocs(
        collection(db, 'commission_sets', setId, 'lists'),
      );
      const lists: Record<CommissionListType, CommissionList | null> = {
        referrer: null,
        sa: null,
        employee: null,
      };
      listsSnap.docs.forEach((d) => {
        const data = d.data() as CommissionList;
        if (
          data.listType === 'referrer' ||
          data.listType === 'sa' ||
          data.listType === 'employee'
        ) {
          lists[data.listType] = data;
        }
      });
      return { set, lists };
    },
  });
}

// ─── Callable ───

const upsertSetFn = httpsCallable<
  {
    setId?: string;
    name: string;
    description?: string | null;
  },
  { setId: string; created: boolean }
>(functions, 'upsertCommissionSet');

export function useUpsertCommissionSet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Parameters<typeof upsertSetFn>[0]) => {
      const r = await upsertSetFn(input);
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['commission_sets'] }),
  });
}

const deleteSetFn = httpsCallable<{ setId: string }, { ok: true }>(
  functions,
  'deleteCommissionSet',
);

export function useDeleteCommissionSet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { setId: string }) => {
      const r = await deleteSetFn(input);
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['commission_sets'] }),
  });
}

const setItemsFn = httpsCallable<
  {
    setId: string;
    listType: CommissionListType;
    defaultType: CommissionType;
    items: CommissionItem[];
  },
  { ok: true; itemCount: number }
>(functions, 'setCommissionItems');

export function useSetCommissionItems() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Parameters<typeof setItemsFn>[0]) => {
      const r = await setItemsFn(input);
      return r.data;
    },
    onSuccess: (_d, vars) => {
      if (!vars) return;
      qc.invalidateQueries({ queryKey: ['commission_sets'] });
      qc.invalidateQueries({
        queryKey: ['commission_sets', 'detail', vars.setId],
      });
    },
  });
}

const assignFn = httpsCallable<
  { categoryId: string; setId: string | null },
  { ok: true }
>(functions, 'assignCommissionSetToCategory');

/** @deprecated v3.2.9 で廃止。`useActivateCommissionSet` を使うこと。 */
export function useAssignCommissionSetToCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Parameters<typeof assignFn>[0]) => {
      const r = await assignFn(input);
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['commission_sets'] });
      qc.invalidateQueries({ queryKey: ['product_categories'] });
    },
  });
}

const activateFn = httpsCallable<
  { setId: string | null },
  { ok: true; activeSetId: string | null }
>(functions, 'activateCommissionSet');

// ─── v3.3.0+: 開拓顧客の SA 報酬加算率設定 (旧「弊社紹介減額率」の反転仕様) ───
const setSelfDevelopedAdditionFn = httpsCallable<
  { setId: string; categoryId: string; rate: number | null },
  { ok: true }
>(functions, 'setSelfDevelopedSaAdditionRate');

export function useSetSelfDevelopedSaAdditionRate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Parameters<typeof setSelfDevelopedAdditionFn>[0]) => {
      const r = await setSelfDevelopedAdditionFn(input);
      return r.data;
    },
    onSuccess: (_d, vars) => {
      if (!vars) return;
      qc.invalidateQueries({ queryKey: ['commission_sets'] });
      qc.invalidateQueries({
        queryKey: ['commission_sets', 'detail', vars.setId],
      });
    },
  });
}

/**
 * 単一の「有効セット」を切替 (v3.2.9 / T5 Phase 3)。
 * null を渡すと全セットを無効化。
 */
export function useActivateCommissionSet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Parameters<typeof activateFn>[0]) => {
      const r = await activateFn(input);
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['commission_sets'] });
    },
  });
}
